import { AfterViewInit, Component, ElementRef, HostListener, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { BaseComponent } from 'src/app/common/base/base.component';
import { RSFPrivileges, RsfService, RSFStatus } from './rsf.service';
import { Observable, Subscription } from 'rxjs';
import { FormStatusEnum, FormType, Roles, Tag } from 'src/app/common/enumerations/enumerations';
import { RsfSideComponent } from './rsf-side/rsf-side.component';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { Kind, RSF, RSFAmendment, RSFButtonAction } from './rsf';
import { CancelDialogComponent } from 'src/app/controls/cancel-dialog/cancel-dialog.component';
import { FormStatus } from 'src/app/services/forms/form-status';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RsfButtonsComponent } from './rsf-buttons/rsf-buttons.component';

@Component({
  selector: 'app-rsf',
  templateUrl: './rsf.component.html',
  styleUrls: ['./rsf.component.scss']
})
export class RsfComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @ViewChild("sidenav") sidenav!: MatSidenav;
  @ViewChild("headerTop") headerTop!: ElementRef;
  @ViewChild(RsfSideComponent) side!: RsfSideComponent;
  @ViewChild(RsfButtonsComponent) public buttons!: RsfButtonsComponent;

  disabled: boolean = true;

  resourceRequired: boolean = false;

  filterCtrl = new FormControl();

  errorMessages: string[] = [];
  headerOnTop: boolean = false;

  valueFilter!: string;
  filter: any;

  rsfUnchanged?: RSF | null;
  rsf?: RSF | null;
  rsfs: RSF[] = [];
  rsfs$!: Observable<RSF[]>;
  rsfsSubs: Subscription = new Subscription;

  rsfAmendment?: RSFAmendment | null;

  loadingMessage!: string;

  privileges = new RSFPrivileges();
  status: RSFStatus = new RSFStatus();

  showAmendmentToggle: boolean = false;
  private resizeTimer: any;
  //////

  constructor(
    public service: RsfService,
    // private notificationService: NotificationService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.setHeights();
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  ngOnInit() {
    this.loadRSFs();

    this.moduleTitle = 'RSF';
    this.checkDisabled();
  }


  /**
   * Loads RSFs from the store and subscribes to data changes.
   * Re-selects the current RSF if it exists in the updated data, unless the form has unsaved
   * edits, so a SignalR-driven refresh cannot overwrite in-progress changes.
   */
  loadRSFs() {
    // Select RSF data stream from store
    this.rsfs$ = this.store.select((state) => state.RSFs.data);

    // Subscribe to RSF data updates
    this.rsfsSubs = this.rsfs$.subscribe((data) => {
      if (data && data.length > 0) {
        // Update local RSF list
        this.rsfs = data;

        // Re-select current RSF if it still exists in updated data
        // Skip while the user has unsaved edits so store refreshes don't wipe them (e.g. Work Description)
        if (this.rsf && this.rsfs.length && !this.isFormDirty()) {
          const rsf = this.rsfs.find((x) => x.id == this.rsf?.id);
          if (rsf) {
            this.rowSelected(rsf);
          }
        }
      }
    });
  }

  /**
   * Updates component state when an RSF row is selected or deselected.
   * Initializes RSF properties, amendments, privileges, and triggers related state updates.
   * @param e The selected RSF, or null/undefined to deselect.
   */
  rowSelected(e?: RSF | null) {
    // Clear history view when deselecting RSF
    if (!e && this.buttons.showHistory) this.buttons.showHistory.value = false;

    const isDifferentRSF = e?.id != this.rsf?.id;
    const nextRsfAmendment = e?.rsfAmendments?.find(x => x.active);
    const isDifferentAmendment = nextRsfAmendment?.id != this.rsfAmendment?.id;
    // Reset for a newly requested amendment, but preserve the user's choice while that amendment refreshes.
    const amendmentViewSelection = isDifferentRSF || isDifferentAmendment ? undefined : this.status?.isViewingAmendment;

    // Scroll to top when switching to a different RSF
    if (isDifferentRSF) this.scrollToTop('sidenav-content');

    // Set current RSF and find active amendment
    this.rsf = e;
    this.rsfAmendment = nextRsfAmendment;
    // Bug 6914: only offer the pending-amendment view while the RSF is actually in an amendment status.
    this.showAmendmentToggle = this.hasPendingAmendment();

    // Display any relevant alerts
    this.showInitialMessages();

    // Cache unchanged copy for change detection and update service reference
    this.rsfUnchanged = e ? this.utils.cloneDeep(e) : null;
    this.service.rsf = this.rsf;

    // Synchronize component state with selected RSF
    this.setStatus(amendmentViewSelection);
    this.setPrivileges();
    this.checkDisabled();
  }

  /**
   * Displays alert messages based on RSF state changes.
   * Shows notifications if form status has changed or worker verification is unsigned.
   */
  showInitialMessages() {
    // Alert when form status changes for the same RSF
    if (
      this.rsf &&
      this.rsfUnchanged?.status != this.rsf?.status &&
      this.rsfUnchanged?.id == this.rsf?.id
    ) {
      this.alert.message(this.getMessage("formStateChanged"), [
        new MessagePlaceholder("{serialNo}", this.rsf.serialNo), new MessagePlaceholder("{status}", this.rsf.formStatus.name ?? 'New'),]);
    }

    // Alert throughout the close-out workflow when its active actions are blocked by Worker Verification.
    const workerVerificationStatuses = [FormStatusEnum.Active, FormStatusEnum.HoldPoint, FormStatusEnum.ShieldingInspections, FormStatusEnum.PpsTesting, FormStatusEnum.PpsTestingComplete, FormStatusEnum.WorkCompleteFollowUps];
    if ((this.rsf?.id != this.rsfUnchanged?.id || this.rsf?.status != this.rsfUnchanged?.status) && this.rsf?.status != null && workerVerificationStatuses.includes(this.rsf.status) && !this.rsf?.workerVerificationSigned) {
      this.alert.message('RSFWorkerVerificationUnsigned');
    }
  }

  /**
   * Clears all error messages from the component state.
   */
  clear() {
    this.errorMessages = [];
  }

  /**
   * Handles RSF button actions after verifying the form can be deactivated.
   * Routes to appropriate handler based on the button action type.
   * @param e The button action to process.
   */
  async buttonClicked(e: RSFButtonAction) {
    // Verify form state before processing any action
    if (await this.canDeactivate()) {
      // Clear history view
      this.buttons.showHistory!.value = false;

      // Route to appropriate action handler
      switch (e) {
        case RSFButtonAction.Create:
          this.create();
          break;
        case RSFButtonAction.Edit:
          this.edit();
          break;
        case RSFButtonAction.Duplicate:
          this.duplicate();
          break;
        case RSFButtonAction.CreateAmendment:
          this.createAmendment();
          break;
        case RSFButtonAction.CreateMinorAmendment:
          this.createMinorAmendment();
          break;
        case RSFButtonAction.EditAmendment:
          this.editAmendment();
          break;
        case RSFButtonAction.Release:
          this.release();
          break;
        case RSFButtonAction.Discard:
          this.discard();
          break;

        default:
          this.alert.message('Action not recognized.');
          break;
      }
    }
  }

  /**
  * Creates a new RSF form with initial properties and persists it to the service.
  * Initializes form with serial number, status, and current user details, and clears any amendment/view
  * state left over from the previously selected RSF.
  * Updates component state and UI after successful creation.
  */
  create() {
    this.service.getSerialNo().toPromise().then((data) => {
      if (data) {
        // Start from a clean RSF context so the new form never renders the previous RSF's amendment or view state.
        this.rsfAmendment = undefined;
        this.showAmendmentToggle = false;
        this.status = { isCreating: true, isEditing: false, isViewingAmendment: false, isCreatingAmendment: false, isEditingAmendment: false, isMinorAmendment: null } as RSFStatus;
        this.rsf = {} as RSF;
        this.rsf.name = data.serialNo;
        this.rsf.serialNo = data.serialNo;
        this.rsf.status = FormStatusEnum.New;
        this.rsf.requesterID = null;
        this.rsf.closedByID = null;
        this.rsf.formStatus = new FormStatus();
        this.rsf.createdByID = this.getCurrentUser()?.id ?? -1;
        this.rsf.rsfRelatedDocuments = [];
        this.rsf.createdOn = new Date();
        this.rsf.estimatedStartOn = new Date();
        this.rsf.estimatedEndOn = new Date();

        this.rsf.rsfResourceRelations = [];
        this.rsf.editingBy = this.getCurrentUser()!;
        this.rsf.kind = Kind.RSF;

        this.side.rsf = this.rsf;
        this.side.selectedRowIndex = -1;
        this.rsf.rsfRoleNotifications = [{ id: 0, roleID: Roles.HP, checked: true, status: -1 }];

        this.service.create(this.rsf).toPromise().then(rsf => {
          this.rsf = rsf;
          if (this.rsf) {
            this.rsfUnchanged = this.utils.cloneDeep(this.rsf);
            this.side.rsf = this.rsf;
            this.disabled = false;
            this.setPrivileges();
            this.status.isCreating = true;
            this.status.isEditing = true;
            if (this.rsf) {
              this.setOpenDocument(FormType.RSF, this.rsf.id);
              this.rsf.retrievedByID = this.getCurrentUser()?.id;
              this.side.selectedRowIndex = this.rsf?.id;
            }
          }
        })
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  /**
   * Handles the "Create Amendment" button click.
   * - If the user holds BOTH canCreateAmendment and canCreateMinorAmendment privileges,
   *   a dialog is opened asking them to choose between Full Amendment and Minor Amendment.
   * - If only canCreateAmendment → proceeds directly to full amendment.
   * - If only canCreateMinorAmendment → proceeds directly to minor amendment (with warning).
   */
  async createAmendment() {
    const canFull = !!this.privileges.canCreateAmendment;
    const canMinor = !!this.privileges.canCreateMinorAmendment;

    if (canFull && canMinor) {
      // Both privileges: show choice dialog
      const choiceDialog = this.dialog.open(YesNoDialogComponent, {
        width: '28em',
        data: {
          // Resolve dialog copy from seeded messages so it can be maintained centrally.
          message: this.getMessage('RSFSelectAmendmentTypeDialog').description,
          icon: 'question',
          value: false,
          changeButtonLabels: true,
          labelButtonCancel: this.getMessage('RSFFullAmendmentButton').description,
          labelButtonAccept: this.getMessage('RSFMinorAmendmentButton').description,
        },
      });

      const choseMinor = await choiceDialog.afterClosed().toPromise();

      if (choseMinor === undefined) {
        // Dialog was dismissed — do nothing
        return;
      }

      if (choseMinor) {
        // User chose Minor Amendment — show warning first
        await this.createMinorAmendmentWithWarning();
      } else {
        // User chose Full Amendment
        this.service.requestAmendment(this.rsf?.id ?? 0).toPromise();
      }
    } else if (canMinor) {
      // Only minor amendment privilege
      await this.createMinorAmendmentWithWarning();
    } else {
      // Only full amendment privilege (original behaviour)
      this.service.requestAmendment(this.rsf?.id ?? 0).toPromise();
    }
  }

  /**
   * Shows the minor-amendment pre-approved-changes warning and, if acknowledged,
   * calls the minor amendment service endpoint.
   */
  private async createMinorAmendmentWithWarning() {
    const warningDialog = this.dialog.open(YesNoDialogComponent, {
      width: '32em',
      data: {
        // Warning text and action labels come from the message catalog.
        message: this.getMessage('RSFMinorAmendmentWarningDialog').description,
        icon: 'warning',
        value: true,
        changeButtonLabels: true,
        labelButtonCancel: this.getMessage('RSFMinorAmendmentCancelButton').description,
        labelButtonAccept: this.getMessage('RSFMinorAmendmentProceedButton').description,
      },
    });

    const confirmed = await warningDialog.afterClosed().toPromise();
    if (confirmed) {
      this.service.requestMinorAmendment(this.rsf?.id ?? 0).toPromise();
    }
  }

  createMinorAmendment() {
    this.service.requestMinorAmendment(this.rsf?.id ?? 0).toPromise();
  }

  async edit() {
    await this.setEditing(true);
    this.status.isEditing = true;
    if (this.rsf)
      this.rsfUnchanged = this.utils.cloneDeep(this.rsf);
  }

  async editAmendment() {
    await this.setEditing(true);
    this.status.isEditingAmendment = true;
    this.status.isViewingAmendment = true;
    if (this.rsf)
      this.rsfUnchanged = this.utils.cloneDeep(this.rsf);
  }

  discard() {
    this.rsf = this.rsfUnchanged;
    this.rowSelected(this.rsf);
  }

  /**
   * Releases the current RSF form and cancels it if it's in New status.
   * Confirms user can deactivate before releasing, then clears editing state and updates UI.
   */
  async release() {
    if (!this.canReleaseEditingForm()) return;

    // Check if form can be deactivated (validates unsaved changes)
    if (await this.canDeactivate()) {
      await this.releaseForm();

      // Cancel new forms and update component state
      if (this.rsf?.status == FormStatusEnum.New) {
        this.service
          .cancel(this.rsf, "New Form Canceled")
          .toPromise()
          .then(() => {
            this.alert.warning('RSF Released and Removed!');
            this.setOpenDocument(FormType.RSF);
            this.setPrivileges();
            this.rsf = null;
          });
      }
    }
  }

  cleanRSF(rsf: RSF) {
    let clonedRSF: RSF = {} as RSF;
    if (rsf) {
      clonedRSF = this.utils.cloneDeep(rsf);
      clonedRSF.wfTable = null;
      clonedRSF.editingBy = null;
      clonedRSF.editingByID = null;
      clonedRSF.updatedOn = null;
      clonedRSF.updatedBy = null;
    }
    return clonedRSF;
  }

  async releaseForm() {
    this.status.isEditing = false;
    this.status.isCreating = false;
    this.status.isEditingAmendment = false;
    this.rsf = this.rsfUnchanged;
    await this.setEditing(false);
  }

  async setEditing(value: boolean) {
    const amendment = this.rsf?.rsfAmendments?.find(x => x.active);
    await this.service.setEditing(this.rsf?.id ?? 0, value, amendment?.id).toPromise();
    this.disabled = !value;
  }

  @HostListener("window:resize")
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth < 768) {
        this.isExpanded = false;
      }
    }, 200);
  }

  //////
  changedFilter() { }
  clearFilter() { }
  selectedFilter() { }
  createDisabled() { }
  editDisabled() { }
  action() { }

  applyFilter(e: any) {
    if (!e)
      this.valueFilter = '';
    this.filter = e;
  }

  /**
   * Updates workflow status flags while optionally preserving the current amendment-view toggle.
   * @param amendmentViewSelection - Existing toggle choice for a refresh of the same RSF.
   */
  setStatus(amendmentViewSelection?: boolean | null) {
    // Find and cache the active amendment
    this.rsfAmendment = this.rsf?.rsfAmendments?.find(x => x.active);

    // Initialize status object
    this.status = new RSFStatus();

    // Set status flags based on form state and current user
    this.status = {
      isCreating: this.rsf?.status == FormStatusEnum.New,
      isEditing: this.rsf?.editingByID == this.currentUser?.id,
      // Default amendment drafting to the pending view so edits are never applied to the approved form.
      // Bug 6914: never show the amendment view once the RSF has left the amendment statuses (e.g. back in PPS Testing).
      isViewingAmendment: this.hasPendingAmendment() && (amendmentViewSelection ?? (
        this.rsf?.status == FormStatusEnum.AmendmentDrafting ||
        this.rsfAmendment?.editingByID == this.currentUser?.id
      )),
      isCreatingAmendment: this.rsf?.status == FormStatusEnum.AmendmentDrafting && this.rsfAmendment?.editingByID == this.currentUser?.id,
      isEditingAmendment: this.rsf?.status == FormStatusEnum.AmendmentDrafting && this.rsfAmendment?.editingByID == this.currentUser?.id,
      isMinorAmendment: this.rsfAmendment?.isMinorAmendment ?? null
    };
  }

  /**
   * Indicates whether the RSF has an active amendment that is still in progress (drafting or approvals).
   * Approved amendments return the RSF to Active or PPS Testing, where the pending-amendment view no longer applies.
   * @returns True when the pending-amendment toggle and view should be available.
   */
  hasPendingAmendment(): boolean {
    const pendingAmendmentStatuses = [
      FormStatusEnum.AmendmentDrafting,
      FormStatusEnum.AmendmentPending,
      FormStatusEnum.AmendmentReviewApprovals,
      FormStatusEnum.AmendmentPendingRequesterConcurrence,
      FormStatusEnum.AmendmentPendingSafetyControls,
    ];
    return !!this.rsfAmendment && pendingAmendmentStatuses.includes(this.rsf?.status ?? 0);
  }

  canReleaseEditingForm(): boolean {
    const isReleaseStatus =
      this.rsf?.status === FormStatusEnum.Draft ||
      this.rsf?.status === FormStatusEnum.AmendmentDrafting;
    const isCurrentEditor =
      this.rsf?.editingByID == this.currentUser?.id ||
      this.rsfAmendment?.editingByID == this.currentUser?.id;

    return !!(
      isReleaseStatus &&
      (this.rsf?.editingBy || this.rsfAmendment?.editingBy) &&
      (isCurrentEditor || this.hasPrivilege(PrivilegeEnum.ReleaseEditingForms))
    );
  }

  /**
   * Switches between the original RSF and its pending amendment.
   * Refreshes input references so child sections rebuild cached view proxies.
   * @param e - True to display the pending amendment.
   */
  isViewingAmendmentChanged(e: boolean) {
    // Replace the status reference so amendment-aware child components receive ngOnChanges.
    this.status = {
      ...this.status,
      isViewingAmendment: e
    } as RSFStatus;

    if (this.rsf) {
      // Refresh both data references so sections with cached proxies reload the selected view.
      this.rsf = { ...this.rsf };
      const activeAmendmentIndex = this.rsf.rsfAmendments?.findIndex(amendment => amendment.active) ?? -1;
      if (activeAmendmentIndex >= 0 && this.rsf.rsfAmendments) {
        const activeAmendment = { ...this.rsf.rsfAmendments[activeAmendmentIndex] };
        this.rsf.rsfAmendments = [...this.rsf.rsfAmendments];
        this.rsf.rsfAmendments[activeAmendmentIndex] = activeAmendment;
        this.rsfAmendment = activeAmendment;
      }
    }

    this.setPrivileges();
    this.checkDisabled();
  }

  /**
   * Evaluates and sets user privileges based on current RSF status and form state.
   * Determines all allowed actions (create, edit, submit, amend, etc.) for the current user and form.
   * Amendments can be created from Active, Active/Hold Point, or PPS Testing.
   */
  setPrivileges() {
    // Find active amendment if present
    this.rsfAmendment = this.rsf?.rsfAmendments?.find(x => x.active);

    // Extract form status and current editing state
    const status = this.rsf?.status;
    const st = this.status ?? {};

    // Helper function to check user privilege
    const has = (p: PrivilegeEnum) => this.hasPrivilege(p);

    // Status flags - determine current form state
    const isDraft = (status === FormStatusEnum.Draft || status == FormStatusEnum.New);
    const isReleaseDraft = status === FormStatusEnum.Draft;
    const isWorkApprovals = status === FormStatusEnum.WorkApprovals;
    const isPendingReview = status === FormStatusEnum.PendingReview;
    const isActive = status === FormStatusEnum.Active;
    const isHoldPoint = status === FormStatusEnum.HoldPoint;
    const isAmendmentDrafting = status === FormStatusEnum.AmendmentDrafting;
    const isAmendmentPending = status === FormStatusEnum.AmendmentPending;
    const isAmendmentApprovals = status === FormStatusEnum.AmendmentReviewApprovals;
    // Bug 6914: RSFs auto-advanced to PPS Testing can be amended.
    const isPpsTesting = status === FormStatusEnum.PpsTesting;

    // Base privileges - core form operations
    const canCreate = has(PrivilegeEnum.RSFCreate);
    const canCancel = has(PrivilegeEnum.RSFCancel);
    const canUnsubmit = has(PrivilegeEnum.RSFUnsubmit) && (isWorkApprovals || isPendingReview);
    const canRelease = has(PrivilegeEnum.ReleaseEditingForms) && (isReleaseDraft || isAmendmentDrafting);

    // Draft flow - initial form creation and submission
    const canEdit = canCreate && isDraft && (!this.rsf?.editingByID || this.rsf?.editingByID == this.currentUser?.id);
    const canSubmit =
      has(PrivilegeEnum.RSFSubmit) &&
      isDraft &&
      !!(st.isCreating || st.isEditing);

    // Amendment flow - modification workflow for active forms
    const canCreateAmendment = has(PrivilegeEnum.RSFCreateAmendment) && (isActive || isHoldPoint || isPpsTesting);
    const canCreateMinorAmendment = has(PrivilegeEnum.RSFCreateMinorAmendment) && (isActive || isHoldPoint || isPpsTesting);
    const canEditAmendment = has(PrivilegeEnum.RSFCreateAmendment) && isAmendmentDrafting && this.rsfAmendment && (!this.rsfAmendment?.editingByID || this.rsfAmendment?.editingByID == this.currentUser?.id);
    const canSubmitAmendment =
      has(PrivilegeEnum.RSFSubmitAmendment) &&
      isAmendmentDrafting &&
      !!(st.isCreatingAmendment || st.isEditing);
    const canCancelAmendment = has(PrivilegeEnum.RSFCancelAmendment);
    const canUnsubmitAmendment =
      has(PrivilegeEnum.RSFUnsubmitAmendment) && isAmendmentApprovals;

    // Miscellaneous privileges - utility operations
    const canViewAmendment = has(PrivilegeEnum.RSFViewAmendment) || canCreateAmendment || canSubmitAmendment;
    const canDuplicate = has(PrivilegeEnum.RSFDuplicate);
    const canAddHoldPoints = has(PrivilegeEnum.CreateHoldPoints);
    const canAddHoldPointsDraft = has(PrivilegeEnum.CreateHoldPointsDraft);

    // Assign all privileges to component property
    this.privileges = {
      canCreate,
      canEdit,
      canSubmit,
      canCancel,
      canUnsubmit,
      canCreateAmendment,
      canCreateMinorAmendment,
      canEditAmendment,
      canSubmitAmendment,
      canCancelAmendment,
      canUnsubmitAmendment,
      canViewAmendment,
      canDuplicate,
      canRelease,
      canAddHoldPoints,
      canAddHoldPointsDraft,
    } as RSFPrivileges;
  }

  /**
   * Determines if the form editing controls should be disabled based on user privileges and form state.
   * Enables editing only when user is in create/edit mode for base or amendment forms with proper permissions.
   */
  checkDisabled(): void {
    // Default to disabled state
    this.disabled = true;

    // Guard clauses - exit early if required dependencies are missing
    if (!this.currentUser || !this.rsf || !this.privileges) return;

    // Extract current form status and editing state
    const status = this.rsf.status;
    const st = this.status ?? {};

    // Check user privileges for base and amendment editing
    const canEdit = !!this.privileges.canEdit;
    const canEditAmend = !!this.privileges.canEditAmendment;

    // Status gates - determine if form is in an editable state
    const isEditableBase = (status === FormStatusEnum.New || status === FormStatusEnum.Draft) && (st.isCreating || st.isEditing);
    const isEditableAmend = status === FormStatusEnum.AmendmentDrafting && st.isViewingAmendment && (st.isCreatingAmendment || st.isEditingAmendment);

    // Enable when either base or amendment path is allowed
    const enable =
      (isEditableBase && canEdit) || (isEditableAmend && canEditAmend);

    this.disabled = !enable;
  }

  /**
 * Creates a copy of the current RSF form after user confirmation.
 * Duplicates the form data and reloads the side component with the new copy.
 */
  duplicate() {
    // Open confirmation dialog to verify duplication intent
    const dialog = this.dialog.open(YesNoDialogComponent, {
      width: "20em", data: {
        message: "Are you sure you want to copy this form?",
        icon: "warn",
        val: false,
      },
    });

    // Handle dialog result
    dialog.afterClosed().toPromise().then((result) => {
      if (result) {
        // Extract RSF ID and deselect current row
        const id = this.rsf?.id ?? 0;
        this.side.rowUnselected();

        // Call service to duplicate the form
        this.service.duplicate(id, false).toPromise().then(async (rsf) => {
          // Update component with duplicated RSF
          this.rsf = rsf;
          // if (this.rsf?.id)
          //   this.notificationService.createNotification(NotificationType.CreateSCF, this.scfMaster.id, await this.scfService.getEmails(this.scfMaster), 5, this.scfMaster?.locations).toPromise();

          // Reload side component with new RSF data
          if (this.rsf)
            await this.side.getValues(this.rsf);
        })
          .catch((error) => {
            // Handle duplication errors
            console.log(error);
            this.alert.message(
              this.getMessage("defaultErrorMessage")
            );
          });
      }
    });
  }

}
